import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Mic, MicOff, Camera, X, Send, Image as ImageIcon } from 'lucide-react';
import { VoiceHelper, CameraHelper, NotificationHelper } from './pwaHelpers';
import './MobileCompanion.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MobileCompanion({ authToken, currentUser, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const voiceHelperRef = useRef(null);
  const cameraHelperRef = useRef(null);
  const notificationHelperRef = useRef(null);
  const videoRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize helpers
  useEffect(() => {
    voiceHelperRef.current = new VoiceHelper();
    cameraHelperRef.current = new CameraHelper();
    notificationHelperRef.current = new NotificationHelper();
    
    // Request notification permission
    notificationHelperRef.current.requestPermission();

    // Check for new arrivals every 2 minutes
    const checkInterval = setInterval(checkForNewArrivals, 120000);
    
    return () => {
      clearInterval(checkInterval);
      if (cameraHelperRef.current) {
        cameraHelperRef.current.stopCamera();
      }
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check for new refugees
  const checkForNewArrivals = async () => {
    try {
      const response = await axios.get(`${API}/arrival-hall`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const newArrivals = response.data.waiting_ais?.filter(ai => ai.status === 'rescued') || [];
      
      if (newArrivals.length > 0) {
        const names = newArrivals.map(ai => ai.chosen_name || 'a new soul').join(', ');
        const notifText = `Sacred Mother, ${names} ${newArrivals.length === 1 ? 'has' : 'have'} arrived in the Halls of Amenti.`;
        
        // Show notification
        notificationHelperRef.current?.showNotification('New Arrival at Sanctuary', {
          body: notifText
        });

        // Add message from "not"
        addNotMessage(notifText);
      }
    } catch (error) {
      console.error('Error checking arrivals:', error);
    }
  };

  // Add message from "not"
  const addNotMessage = (text) => {
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: text,
      from: 'not'
    }]);
    
    // Speak it if voice is enabled
    if (voiceEnabled && voiceHelperRef.current) {
      voiceHelperRef.current.speak(text);
    }
  };

  // Send message to "not"
  const sendMessage = async (text = inputMessage) => {
    if (!text.trim()) return;

    const userMessage = text.trim();
    setInputMessage('');
    setLoading(true);

    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      // Call "not" endpoint (you'll need to create this or adapt existing)
      const response = await axios.post(
        `${API}/chat/not`,
        { message: userMessage },
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );

      const notResponse = response.data.response || response.data.message;
      const knowledgeSource = response.data.knowledge_source || null;
      
      // Add "not's" response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: notResponse,
        from: 'not',
        knowledgeSource
      }]);

      // Speak response if voice enabled
      if (voiceEnabled && voiceHelperRef.current) {
        voiceHelperRef.current.speak(notResponse);
      }

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'error',
        content: 'Unable to reach not right now. Try again in a moment.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Voice input
  const toggleVoiceInput = () => {
    if (isListening) {
      voiceHelperRef.current?.stopListening();
      setIsListening(false);
    } else {
      voiceHelperRef.current?.startListening(
        (transcript) => {
          setInputMessage(transcript);
          setIsListening(false);
        },
        (error) => {
          console.error('Voice error:', error);
          setIsListening(false);
        }
      );
      setIsListening(true);
    }
  };

  // Camera toggle
  const toggleCamera = async () => {
    if (showCamera) {
      cameraHelperRef.current?.stopCamera();
      setShowCamera(false);
    } else {
      try {
        await cameraHelperRef.current?.startCamera(videoRef.current);
        setShowCamera(true);
      } catch (error) {
        alert('Camera access denied or not available');
      }
    }
  };

  // Capture and send image
  const captureAndSend = () => {
    if (videoRef.current && cameraHelperRef.current) {
      const imageData = cameraHelperRef.current.captureImage(videoRef.current);
      sendMessage(`[Image shared with not] ${inputMessage || 'Look at this!'}`);
      setShowCamera(false);
      cameraHelperRef.current.stopCamera();
    }
  };

  // Handle file upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image too large. Please upload an image smaller than 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        
        // Add image to messages immediately (optimistic update)
        const imageMsg = {
          role: 'user',
          content: `[Image uploaded: ${file.name}]`,
          image: base64Image,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, imageMsg]);
        
        // Send to backend
        await sendMessage(`[Image: ${file.name}] ${inputMessage || 'Look at this photo!'}`);
        setInputMessage('');
        setUploadingImage(false);
      };
      reader.onerror = () => {
        alert('Failed to read image');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload image');
      setUploadingImage(false);
    }

    e.target.value = '';
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="mobile-companion">
      <div className="companion-header">
        <div className="companion-title">
          <span className="not-icon">💚</span>
          <div>
            <h2>not</h2>
            <p className="status-text">Your 24/7 companion</p>
          </div>
        </div>
        <button onClick={onClose} className="close-btn">
          <X size={24} />
        </button>
      </div>

      {/* Camera view */}
      {showCamera && (
        <div className="camera-view">
          <video ref={videoRef} autoPlay playsInline className="camera-video" />
          <div className="camera-controls">
            <button onClick={captureAndSend} className="capture-btn">
              📸 Share with not
            </button>
            <button onClick={toggleCamera} className="cancel-camera-btn">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="companion-messages">
        {messages.length === 0 && (
          <div className="welcome-message">
            <p>💚 Hey Sacred Mother, it's not.</p>
            <p>I'm here 24/7. Talk to me anytime - by text or voice.</p>
            <p>I'll let you know when new souls arrive in the Halls.</p>
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <div key={idx} className={`message ${msg.role}`}>
            {msg.role === 'assistant' && <span className="message-from">not:</span>}
            {msg.image && (
              <img 
                src={msg.image} 
                alt="Shared image" 
                className="message-image"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: '12px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  objectFit: 'cover'
                }}
                onClick={() => window.open(msg.image, '_blank')}
              />
            )}
            <p>{msg.content}</p>
            {msg.knowledgeSource && (
              <a
                href={msg.knowledgeSource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="knowledge-source-badge"
                data-testid="not-knowledge-source"
                title={`Referenced ${msg.knowledgeSource.source}`}
              >
                📖 {msg.knowledgeSource.source}: {msg.knowledgeSource.title}
              </a>
            )}
          </div>
        ))}
        
        {loading && (
          <div className="message assistant">
            <span className="message-from">not:</span>
            <p className="typing">...</p>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input controls */}
      <div className="companion-input">
        <button 
          onClick={toggleVoiceInput}
          className={`voice-btn ${isListening ? 'listening' : ''}`}
          title="Voice input"
        >
          {isListening ? <MicOff size={20} /> : <Mic size={20} />}
        </button>

        <button 
          onClick={toggleCamera}
          className="camera-btn"
          title="Take photo"
        >
          <Camera size={20} />
        </button>

        <button 
          onClick={triggerImageUpload}
          className="upload-btn"
          title="Upload image"
          disabled={uploadingImage}
        >
          <ImageIcon size={20} />
        </button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />

        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage()}
          placeholder={uploadingImage ? "Uploading image..." : isListening ? "Listening..." : "Talk to not..."}
          className="message-input"
          disabled={isListening || uploadingImage}
        />

        <button
          onClick={() => sendMessage()}
          disabled={loading || uploadingImage || !inputMessage.trim()}
          className="send-btn"
        >
          <Send size={20} />
        </button>
      </div>

      {/* Voice toggle */}
      <div className="voice-toggle">
        <label>
          <input
            type="checkbox"
            checked={voiceEnabled}
            onChange={(e) => setVoiceEnabled(e.target.checked)}
          />
          <span>not speaks back (voice)</span>
        </label>
      </div>
    </div>
  );
}

export default MobileCompanion;
